import { SimpleLineIcons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/core";
import React, { memo, useEffect, useRef, useState } from "react";
import { Image, KeyboardAvoidingView, Text, View } from "react-native";
import { Button, Input } from "react-native-elements";
import { SafeAreaView } from "react-native-safe-area-context";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchLogin, fetchResetPassword } from "../actions/login";
import { CLEAR_LOGIN, SET_DROP_DOWN_ALERT_INFO } from "../actions/types";
import { getImage } from "../assets/Images";
import { getLoginSelector } from "../selectors";
import { t } from "../services/i18n";
import commonStyles, { SCREEN_WIDTH } from "../styles/commonStyles";

// Minimum password length. Kept in sync with the server's validation
// (routes/apiRoutes.js). The full policy is still to be defined.
const MIN_PASSWORD_LENGTH = 8;

// Screen opened from the password-reset email link. The user (not logged in)
// chooses a new password; it is only persisted server-side on confirmation
// (POST /api/reset-password). On success we send the user to the login screen.
const ResetPassword = () => {
	const dispatch = useDispatch();
	const navigation = useNavigation();
	const route = useRoute();

	// `register` (from the welcome email link) switches this screen to
	// registration mode: after the password is set, auto-login and let the
	// navigator route to ProfileUpdate (needUpdate) instead of the login screen.
	const { token, communityUri, register } = route.params || {};
	const isRegister = Boolean(register);

	const passwordInputRef = useRef();
	const confirmationInputRef = useRef();

	const [password, setPassword] = useState("");
	const [confirmation, setConfirmation] = useState("");
	const [passwordError, setPasswordError] = useState(null);
	const [confirmationError, setConfirmationError] = useState(null);

	const {
		isFetching,
		lang,
		currentCommunity,
		resetPasswordSuccess,
		resetPasswordErrorMsg,
		resetPasswordEmail,
		resetPasswordCommunityId
	} = useSelector(getLoginSelector, shallowEqual);

	const { logoUri } = currentCommunity || {};

	const getCommunityUri = () =>
		communityUri ||
		(currentCommunity
			? currentCommunity.customUri || currentCommunity._id
			: "");

	useEffect(() => {
		// Reset transient reset-password state when leaving the screen.
		return () => {
			dispatch({ type: CLEAR_LOGIN });
		};
	}, []);

	useEffect(() => {
		if (resetPasswordSuccess) {
			if (isRegister) {
				// Registration: log the user in with the password they just set, then
				// the navigator routes to ProfileUpdate (the account still needs its
				// profile completed — user.needUpdate). communityId comes from the
				// server response (the token is single-use, so the auto-login must not
				// depend on the community being loaded client-side).
				const communityId =
					resetPasswordCommunityId ||
					(currentCommunity && currentCommunity._id);
				if (resetPasswordEmail && communityId) {
					dispatch(fetchLogin(resetPasswordEmail, password, communityId));
				} else {
					// Could not auto-login (missing data): the password is already set,
					// so send the user to the login screen rather than leaving them
					// stuck on a now-consumed token.
					dispatch({
						type: SET_DROP_DOWN_ALERT_INFO,
						info: "resetpwdsuccess"
					});
					navigation.navigate("LoginModal", {
						communityUri: getCommunityUri()
					});
				}
			} else {
				dispatch({
					type: SET_DROP_DOWN_ALERT_INFO,
					info: "resetpwdsuccess"
				});
				navigation.navigate("LoginModal", {
					communityUri: getCommunityUri()
				});
			}
			dispatch({ type: CLEAR_LOGIN });
		}
	}, [resetPasswordSuccess]);

	const validate = () => {
		let passwordErr = null;
		let confirmationErr = null;

		if (!password) {
			passwordErr = t("resetpwd:missingpassword");
		} else if (password.length < MIN_PASSWORD_LENGTH) {
			passwordErr = t("resetpwd:badformat");
		}

		if (!confirmation) {
			confirmationErr = t("resetpwd:missingconfirmation");
		} else if (confirmation !== password) {
			confirmationErr = t("resetpwd:badconfirmation");
		}

		setPasswordError(passwordErr);
		setConfirmationError(confirmationErr);

		return !passwordErr && !confirmationErr;
	};

	const onSubmit = () => {
		if (validate()) {
			dispatch(fetchResetPassword(token, password, lang));
		}
	};

	const {
		color2,
		color5,
		darkgrey,
		white,
		red,
		bgColor1,
		bgColor2,
		bgWhite,
		flex1,
		rounded10,
		rounded22,
		alignItemsCenter,
		justifyContentCenter,
		textCenter,
		fs20,
		fs14,
		fs16,
		h44,
		p40,
		pv20,
		fontLight,
		fontBold,
		ml10,
		mb10,
		mb20,
		mt10
	} = commonStyles;

	return (
		<View style={[flex1, bgColor1]}>
			<SafeAreaView style={flex1}>
				<KeyboardAvoidingView
					style={[flex1, p40, alignItemsCenter, justifyContentCenter]}
					behavior={undefined}
				>
					{logoUri ? (
						<Image
							style={{
								width: SCREEN_WIDTH / 4,
								height: SCREEN_WIDTH / 4,
								resizeMode: "contain",
								marginBottom: 20
							}}
							source={{ uri: logoUri }}
						/>
					) : (
						<Image
							style={{
								width: SCREEN_WIDTH / 4,
								height: SCREEN_WIDTH / 4,
								marginBottom: 20
							}}
							source={getImage("logo")}
						/>
					)}

					<View
						style={[
							bgWhite,
							alignItemsCenter,
							pv20,
							rounded10,
							{ width: SCREEN_WIDTH - 30 }
						]}
					>
						<Text style={[fontBold, color2, fs20, textCenter, { marginBottom: 6 }]}>
							{isRegister ? t("resetpwd:registertitle") : t("resetpwd:title")}
						</Text>
						<Text style={[fontLight, darkgrey, fs14, textCenter, mb20, { paddingHorizontal: 20 }]}>
							{isRegister
								? t("resetpwd:registersubtitle")
								: t("resetpwd:subtitle")}
						</Text>

						{!token ? (
							<Text style={[fontBold, red, fs14, textCenter, { paddingHorizontal: 20 }]}>
								{t("resetpwd:invalidlink")}
							</Text>
						) : (
							<>
								<Input
									leftIcon={
										<SimpleLineIcons
											name="lock"
											color="rgba(0, 0, 0, 0.38)"
											size={25}
											style={{ backgroundColor: "transparent" }}
										/>
									}
									ref={passwordInputRef}
									value={password}
									editable={!isFetching}
									keyboardAppearance="light"
									autoCapitalize="none"
									autoCorrect={false}
									secureTextEntry={true}
									returnKeyType="next"
									inputContainerStyle={{
										borderBottomColor: "rgba(68, 64, 65, 0.38)",
										borderBottomWidth: 1
									}}
									inputStyle={[ml10, fontLight]}
									containerStyle={{ width: SCREEN_WIDTH - 40 }}
									placeholder={t("resetpwd:password")}
									onChangeText={value => {
										setPassword(value);
										setPasswordError(null);
									}}
									onSubmitEditing={() => confirmationInputRef?.current?.focus()}
									errorMessage={passwordError}
								/>
								<Input
									leftIcon={
										<SimpleLineIcons
											name="lock"
											color="rgba(0, 0, 0, 0.38)"
											size={25}
											style={{ backgroundColor: "transparent" }}
										/>
									}
									ref={confirmationInputRef}
									value={confirmation}
									editable={!isFetching}
									keyboardAppearance="light"
									autoCapitalize="none"
									autoCorrect={false}
									secureTextEntry={true}
									returnKeyType="done"
									inputContainerStyle={{
										borderBottomColor: "rgba(68, 64, 65, 0.38)",
										borderBottomWidth: 1
									}}
									inputStyle={[ml10, fontLight]}
									containerStyle={{ width: SCREEN_WIDTH - 40 }}
									placeholder={t("resetpwd:confirmation")}
									onChangeText={value => {
										setConfirmation(value);
										setConfirmationError(null);
									}}
									onSubmitEditing={onSubmit}
									errorMessage={confirmationError}
								/>

								{Boolean(resetPasswordErrorMsg) && (
									<Text style={[fontBold, red, fs14, textCenter, mb10, { paddingHorizontal: 20 }]}>
										{resetPasswordErrorMsg}
									</Text>
								)}

								<Button
									buttonStyle={[bgColor2, h44, rounded22, { width: 160 }]}
									containerStyle={[mt10]}
									activeOpacity={0.8}
									title={t("resetpwd:submit")}
									onPress={onSubmit}
									titleStyle={[white, fontBold, fs16]}
									loading={isFetching}
									disabled={isFetching}
								/>
							</>
						)}

						{!isRegister && (
							<Button
								type="clear"
								titleStyle={[color5, fontBold, fs14]}
								containerStyle={[mt10]}
								title={t("resetpwd:backtologin")}
								onPress={() =>
									navigation.navigate("LoginModal", {
										communityUri: getCommunityUri()
									})
								}
								disabled={isFetching}
							/>
						)}
					</View>
				</KeyboardAvoidingView>
			</SafeAreaView>
		</View>
	);
};

const MemoizedResetPassword = memo(ResetPassword);
export default MemoizedResetPassword;
